import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { ArticleForm } from "@/components/articles/article-form";
import { adminApi } from "@/lib/admin-api";
import type { Article } from "@/types";

export function ArticleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const articleId = Number(id);

  const [article, setArticle] = useState<Article | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchArticle() {
      try {
        const data = await adminApi.articles.get(articleId);
        setArticle(data);
      } catch (err) {
        toast.error("Failed to load article", {
          description: err instanceof Error ? err.message : undefined,
        });
        navigate("/articles");
      } finally {
        setIsLoading(false);
      }
    }
    fetchArticle();
  }, [articleId, navigate]);

  async function handleSubmit(data: Partial<Article>) {
    setIsSaving(true);
    try {
      await adminApi.articles.update(articleId, data);
      toast.success("Article updated successfully");
      navigate("/articles");
    } catch (err) {
      toast.error("Failed to update article", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={`Edit: ${article.title}`}
        description="Update article details"
        actions={
          <Button variant="outline" onClick={() => navigate("/articles")}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Articles
          </Button>
        }
      />
      <ArticleForm
        initialData={article}
        onSubmit={handleSubmit}
        isLoading={isSaving}
      />
    </div>
  );
}
